import React from 'react';
import { get, find } from 'lodash';
import { FormattedMessage, FormattedNumber, injectIntl } from 'react-intl';
import { LineItem } from '../elements-forms/line-item';
import { TooltipData } from '../elements-tooltip/tooltip';
import {
  RevisedCalculator,
  TableLabel,
  PrepareDiscountFields,
  BorderBottomLineItem,
  BorderTopLineItem
} from '../elements-forms/line-item-helpers';

const CancelSummaryCalculatorFnb = ({ bookingObj, refundBookingObj, currencyType, intl }) => {
  const {
    listing = {},
    guest_payment_amount,
    discounts,
    base_subtotal,
    gratuity,
    sales_tax
  } = bookingObj;

  // handling cancellations through contracts vs cancellations through bookings
  const guestRefundAmounts = get(refundBookingObj, 'delta.guest', {});
  const spaceRentalRefundObj = get(refundBookingObj, 'delta.space_rentals', {});

  const refundSubtotal = parseFloat(spaceRentalRefundObj.guest_subtotal_payment_amount || refundBookingObj.cancel_guest_subtotal_refund || 0).toFixed(2);
  const refundBaseSubtotal = parseFloat(spaceRentalRefundObj.base_subtotal || refundBookingObj.cancel_guest_base_subtotal_refund || 0).toFixed(2);
  const refundTax = parseFloat(spaceRentalRefundObj.sales_tax || refundBookingObj.cancel_guest_sales_tax_refund || 0).toFixed(2);
  const refundTip = parseFloat(spaceRentalRefundObj.gratuity || refundBookingObj.cancel_guest_gratuity_refund || 0).toFixed(2);
  const feeTotal = parseFloat(bookingObj.guest_payment_processing_fee).toFixed(2);
  const feeRefund = parseFloat(guestRefundAmounts.fee || refundBookingObj.cancel_guest_fee_refund || 0).toFixed(2);
  const refundEntitlement = parseFloat(spaceRentalRefundObj.entitlement_discounts || refundBookingObj.cancel_guest_entitlement_refund || 0).toFixed(2);
  const refundTotal = parseFloat(guestRefundAmounts.payment || refundBookingObj.cancel_guest_refund || 0).toFixed(2);

  const subtotalLabel = 'FOOD_BEV' === listing.fandb_applies_to
    ? intl.formatMessage({ id: 'inbox_id_cancel_booking.cancel_summary_calculator_fnb.food_bar_tab', defaultMessage: 'Food and bar tab' })
    : intl.formatMessage({ id: 'inbox_id_cancel_booking.cancel_summary_calculator_fnb.bar_tab', defaultMessage: 'Bar tab' });
  const spaceRentalContract = find(refundBookingObj.contracts, { type: 'SPACE_RENTAL' }) || {};
  const cancelDetails = get(spaceRentalContract, 'meta.cancel_details', {});
  const refundPercent = cancelDetails.refund_percentage || refundBookingObj.refund_percentage;

  return (
    <RevisedCalculator>
      <TableLabel
        firstCaption={{
          value: intl.formatMessage({ id: "inbox_id_cancel_booking.cancel_summary_calculator_fnb.price", defaultMessage: "Price" }),
          optionalClass: "table-label"
        }}
        secondCaption={{
          value: intl.formatMessage({ id: "inbox_id_cancel_booking.cancel_summary_calculator_fnb.refund_amount", defaultMessage: "Refund amount" }),
          optionalClass: "right-data table-label"
        }}
      />
      <LineItem
        label={{ content: subtotalLabel, attributes: { "data-testing-id": "cancel-fnb-label" } }}
        value={{ amount: parseFloat(bookingObj.subtotal_amount.toFixed(2)), attributes: { "data-testing-id": "cancel-fnb-value" } }}
        optionalValue={{ amount: refundSubtotal, attributes: { "data-testing-id": "cancel-fnb-optional-value" } }}
        currencyType={currencyType}
      />
      <BorderTopLineItem />
      <LineItem
        label={{
          content: intl.formatMessage({ id: "inbox_id_cancel_booking.cancel_summary_calculator_fnb.subtotal", defaultMessage: "Subtotal" }),
          optionalClass: "fnb-subtotal-spacing indent-fnb-label top-vertical-align"
        }}
        value={{
          amount: parseFloat(base_subtotal).toFixed(2),
          optionalClass: "fnb-subtotal-spacing top-vertical-align",
          attributes: {
            "data-testing-id": "cancel-fnb-subtotal-value"
          }
        }}
        optionalValue={{
          customValue:
            <div className="margin-md-bottom">
              <FormattedNumber value={refundBaseSubtotal} currency={currencyType} style={"currency"} />
              <div>
                {0 < refundBaseSubtotal
                  ? <FormattedMessage id="inbox_id_cancel_booking.cancel_summary_calculator_fnb.refund_subtotal" defaultMessage="({refundPercent} refund)" values={{ refundPercent: refundPercent }} />
                  : <FormattedMessage id="inbox_id_cancel_booking.cancel_summary_calculator_fnb.refund_subtotal_no_refund" defaultMessage="(No refund)" />
                }

              </div>
            </div>,
          optionalClass: "fnb-subtotal-spacing",
          attributes: {
            "data-testing-id": "cancel-fnb-subtotal-optional-value"
          }
        }}
        currencyType={currencyType}
        customClass="fnb-font-size"
      />
      <LineItem
        label={{
          content: intl.formatMessage({ id: "inbox_id_cancel_booking.cancel_summary_calculator_fnb.tax", defaultMessage: "Tax" }),
          optionalClass: "indent-fnb-label top-vertical-align"
        }}
        value={{
          amount: parseFloat(sales_tax).toFixed(2),
          optionalClass: "top-vertical-align",
          attributes: {
            "data-testing-id": "cancel-fnb-tax-value"
          }
        }}
        optionalValue={{
          customValue:
            <div className="margin-md-bottom">
              <FormattedNumber value={refundTax} currency={currencyType} style={"currency"} />
              <div>
                {0 < refundTax
                  ? <FormattedMessage id="inbox_id_cancel_booking.cancel_summary_calculator_fnb.refund_tax" defaultMessage="(100% refund)" />
                  : <FormattedMessage id="inbox_id_cancel_booking.cancel_summary_calculator_fnb.refund_tax_no_refund" defaultMessage="(No refund)" />
                }
              </div>
            </div>,
          optionalClass: "top-vertical-align",
          attributes: {
            "data-testing-id": "cancel-fnb-tax-optional-value"
          }
        }}
        currencyType={currencyType}
        customClass="fnb-font-size"
      />
      <LineItem
        label={{
          content: intl.formatMessage({ id: "inbox_id_cancel_booking.cancel_summary_calculator_fnb.tip", defaultMessage: "Tip" }),
          optionalClass: "indent-fnb-label top-vertical-align"
        }}
        value={{
          amount: gratuity,
          optionalClass: "top-vertical-align",
          attributes: {
            "data-testing-id": "cancel-fnb-tip-value"
          }
        }}
        optionalValue={{
          customValue:
            <div className="margin-md-bottom">
              <FormattedNumber value={refundTip} currency={currencyType} style={"currency"} />
              <div>
                {0 < refundTip
                  ? <FormattedMessage id="inbox_id_cancel_booking.cancel_summary_calculator_fnb.refund_tip" defaultMessage="(100% refund)" />
                  : <FormattedMessage id="inbox_id_cancel_booking.cancel_summary_calculator_fnb.refund_tix_no_refund" defaultMessage="(No refund)" />
                }
              </div>
            </div>,
          optionalClass: "top-vertical-align",
          attributes: {
            "data-testing-id": "cancel-fnb-tip-optional-value"
          }
        }}
        currencyType={currencyType}
        customClass="fnb-font-size"
      />
      <BorderTopLineItem />
      {
        discounts.map((discount) => {
          const currentDiscount = PrepareDiscountFields(discount);
          return (
            <LineItem
              label={{
                content: currentDiscount.content,
                optionalClass: "top-vertical-align"
              }}
              value={{
                amount: parseFloat(`-${currentDiscount.value}`).toFixed(2),
                optionalClass: "top-vertical-align"
              }}
              optionalValue={{
                amount: `-${refundEntitlement}`,
                optionalClass: "top-vertical-align"
              }}
              currencyType={currencyType}
              customClass="discount-row"
            />
          );
        })
      }
      <LineItem
        label={{
          content: intl.formatMessage({ id: "inbox_id_cancel_booking.cancel_summary_calculator_fnb.processing", defaultMessage: "Processing" }),
          optionalTooltip: TooltipData.PROCESSING_FEE,
          optionalClass: "fnb-processing-fee top-vertical-align"
        }}
        value={{
          amount: feeTotal,
          optionalClass: "fnb-processing-fee top-vertical-align",
          attributes: {
            "data-testing-id": "cancel-fnb-processing-fee-value"
          }
        }}
        optionalValue={{
          customValue:
            <div className="margin-md-bottom">
              <FormattedNumber value={feeRefund} currency={currencyType} style={"currency"} />
              <div className="text-small">
                {0 < feeRefund
                  ? <FormattedMessage id="inbox_id_cancel_booking.cancel_summary_calculator_fnb.fee_refund" defaultMessage="(100% refund)" />
                  : <FormattedMessage id="inbox_id_cancel_booking.cancel_summary_calculator_fnb.fee_refund_no_refund" defaultMessage="(No refund)" />
                }
              </div>
            </div>,
          optionalClass: "fnb-processing-fee top-vertical-align",
          attributes: {
            "data-testing-id": "cancel-fnb-processing-fee-optional-value"
          }
        }}
        currencyType={currencyType}
      />
      <BorderTopLineItem />
      <LineItem
        label={{
          content: intl.formatMessage({ id: "inbox_id_cancel_booking.cancel_summary_calculator_fnb.total", defaultMessage: "Total" })
        }}
        value={{
          amount: parseFloat(guest_payment_amount).toFixed(2),
          optionalClass: "remove-vertical-align",
          attributes: {
            "data-testing-id": "cancel-fnb-total-value"
          }
        }}
        optionalValue={{
          amount: refundTotal,
          optionalClass: `total-row-spacing ${guestRefundAmounts && "updated-total-price"}`,
          attributes: {
            "data-testing-id": "cancel-fnb-total-optional-value"
          }
        }}
        currencyType={currencyType}
      />
      <BorderTopLineItem />
      <LineItem
        label={{
          content: intl.formatMessage({ id: "inbox_id_cancel_booking.cancel_summary_calculator_fnb.refund_amount", defaultMessage: "Refund amount" })
        }}
        value={{}}
        optionalValue={{
          amount: refundTotal,
          optionalClass: "price-difference-spacing",
          attributes: {
            "data-testing-id": "cancel-fnb-price-difference-optional-value"
          }
        }}
        customClass="price-difference h5"
        currencyType={currencyType}
      />
    </RevisedCalculator>
  );
};

const CancelSummaryCalculator = ({ bookingObj, refundBookingObj, currencyType, intl }) => {
  const {
    discounts,
    addtl_charges,
    guest_payment_amount
  } = bookingObj;
  // for fee
  const feeTotal = parseFloat(bookingObj.guest_payment_processing_fee).toFixed(2);

  // discounts/charges are an array, but we cant relay on order, so made an object with types as keys
  const otherDiscounts = [];

  // handling cancellations through contracts vs cancellations through bookings
  const spaceRentalContract = find(refundBookingObj.contracts, { type: 'SPACE_RENTAL' }) || {};
  const cancelDetails = get(spaceRentalContract, 'meta.cancel_details', {});
  const refundPercent = cancelDetails.refund_percentage || refundBookingObj.refund_percentage;
  const spaceRentalRefundObj = get(refundBookingObj, 'delta.space_rentals', {});
  const guestRefundAmounts = get(refundBookingObj, 'delta.guest', {});

  const guestSubtotalRefund = spaceRentalRefundObj.guest_subtotal_payment_amount || refundBookingObj.cancel_guest_subtotal_refund || 0;
  const cleaningFeeRefund = get(refundBookingObj, 'delta.cleaning_fee', 0) || refundBookingObj.cancel_guest_cleaning_fee_refund || 0;
  const dayRateDiscountRefund = spaceRentalRefundObj['8hr_discounts'] || refundBookingObj.cancel_guest_day_rate_discount_refund || 0;
  const entitlementDiscountRefund = spaceRentalRefundObj.entitlement_discounts || refundBookingObj.cancel_guest_entitlement_refund || 0;
  const feeRefund = guestRefundAmounts.fee || refundBookingObj.cancel_guest_fee_refund || 0;
  const refundTotal = parseFloat(guestRefundAmounts.payment || refundBookingObj.cancel_guest_refund || 0).toFixed(2);
  return (
    <RevisedCalculator>
      <TableLabel
        firstCaption={{
          value: intl.formatMessage({ id: 'inbox_id_cancel_booking.cancel_summary_calculator.price', defaultMessage: 'Price' }),
          optionalClass: 'table-label'
        }}
        secondCaption={{
          value: intl.formatMessage({ id: 'inbox_id_cancel_booking.cancel_summary_calculator.refund', defaultMessage: 'Refund amount' }),
          optionalClass: 'right-data table-label'
        }}
      />
      <LineItem
        label={{
          content: intl.formatMessage({ id: 'inbox_id_cancel_booking.cancel_summary_calculator.subtotal', defaultMessage: 'Subtotal' }),
          optionalClass: 'top-vertical-align'
        }}
        value={{
          amount: parseFloat(bookingObj.subtotal_amount).toFixed(2),
          optionalClass: 'top-vertical-align',
          attributes: {
            'data-testing-id': 'cancel-subtotal-value'
          }
        }}
        optionalValue={{
          customValue:
            <div className="margin-md-bottom">
              <FormattedNumber value={parseFloat(guestSubtotalRefund).toFixed(2)} currency={currencyType} style={'currency'} />
              <div className="text-small">
                <FormattedMessage
                  id='inbox_id_cancel_booking.cancel_booking_main.refund'
                  defaultMessage='{refund}'
                  values={{
                    refund: 0 < guestSubtotalRefund
                      ? <FormattedMessage id='inbox_id_cancel_booking.cancel_booking_main.percent_refund' defaultMessage='({percent} refund)' values={{ percent: refundPercent }} />
                      : <FormattedMessage id='inbox_id_cancel_booking.cancel_booking_main.no_refund' defaultMessage='(No refund)' />
                  }}
                />
              </div>
            </div>,
          attributes: {
            'data-testing-id': 'cancel-subtotal-optional-value'
          }
        }}
        currencyType={currencyType}
      />
      {
        discounts.map((discount) => {
          const currentDiscount = PrepareDiscountFields(discount);
          const discountLineItem = (
            <LineItem
              label={{
                content: currentDiscount.content,
                optionalClass: 'top-vertical-align'
              }}
              value={{
                amount: parseFloat(`-${currentDiscount.value}`).toFixed(2),
                optionalClass: 'top-vertical-align',
                attributes: {
                  'data-testing-id': 'cancel-discount-value'
                }
              }}
              optionalValue={{
                amount: 'DAY_RATE_DISCOUNT' === discount.type ? `-${parseFloat(dayRateDiscountRefund).toFixed(2)}` : `-${parseFloat(entitlementDiscountRefund).toFixed(2)}`,
                optionalClass: 'top-vertical-align',
                attributes: {
                  'data-testing-id': 'cancel-discount-optional-value'
                }
              }}
              currencyType={currencyType}
              customClass="discount-row"
              attributes={{
                'data-testing-id': 'cancel-discount'
              }}
            />
          );

          if ('DAY_RATE_DISCOUNT' === discount.type) {
            return discountLineItem;
          }

          otherDiscounts.push(discountLineItem);
        })
      }
      {
        addtl_charges.map((charge) => {
          // always refund 100% of the cleaning fee
          return (
            <LineItem
              label={{
                content: charge.charge_str,
                ...(TooltipData[charge.type] && { optionalTooltip: TooltipData[charge.type] }),
                optionalClass: 'top-vertical-align'
              }}
              value={{
                amount: parseFloat(charge.value).toFixed(2),
                optionalClass: 'top-vertical-align',
                attributes: {
                  'data-testing-id': 'cancel-additional-charge-value'
                }
              }}
              optionalValue={{
                customValue:
                  <div className="margin-md-bottom">
                    <FormattedNumber value={parseFloat(cleaningFeeRefund).toFixed(2)} currency={currencyType} style={'currency'} />
                    <div className="text-small">
                      <FormattedMessage
                        id='inbox_id_cancel_booking.cancel_booking_main.percent_refund_text'
                        defaultMessage='({percent} refund)'
                        values={{
                          percent: <FormattedNumber value={1} style='percent' />
                        }}
                      />
                    </div>
                  </div>,
                optionalClass: 'top-vertical-align',
                attributes: {
                  'data-testing-id': 'cancel-additional-charge-optional-value'
                }
              }}
              currencyType={currencyType}
              attributes={{
                'data-testing-id': 'cancel-additional-charge'
              }}
            />
          );
        })
      }
      {
        otherDiscounts.map((lineItem => {
          return lineItem;
        }))
      }
      <LineItem
        label={{
          content: intl.formatMessage({ id: 'inbox_id_cancel_booking.cancel_summary_calculator.processing', defaultMessage: 'Processing' }),
          optionalTooltip: TooltipData.PROCESSING_FEE,
          optionalClass: 'top-vertical-align'
        }}
        value={{
          amount: feeTotal,
          optionalClass: 'top-vertical-align',
          attributes: {
            'data-testing-id': 'cancel-processing-fee-value'
          }
        }}
        optionalValue={{
          customValue:
            <div className="margin-md-bottom">
              <FormattedNumber value={parseFloat(feeRefund).toFixed(2)} currency={currencyType} style={'currency'} />
              <div className="text-small">
                <FormattedMessage
                  id='inbox_id_cancel_booking.cancel_booking_main.refund_text'
                  defaultMessage='{refund}'
                  values={{
                    refund: 0 < feeRefund
                      ? <FormattedMessage id='inbox_id_cancel_booking.cancel_booking_main.100_refund' defaultMessage='(100% refund)' />
                      : <FormattedMessage id='inbox_id_cancel_booking.cancel_booking_main.no_refund_text' defaultMessage='(No refund)' />
                  }}
                />
              </div>
            </div>,
          optionalClass: 'top-vertical-align margin-sm-bottom',
          attributes: {
            'data-testing-id': 'cancel-processing-fee-optional-value'
          }
        }}
        currencyType={currencyType}
      />
      <BorderBottomLineItem />
      <LineItem
        label={{ content: intl.formatMessage({ id: 'inbox_id_cancel_booking.cancel_summary_calculator.total', defaultMessage: 'Total' }) }}
        value={{
          amount: parseFloat(guest_payment_amount).toFixed(2),
          optionalClass: 'remove-vertical-align',
          attributes: {
            'data-testing-id': 'cancel-total-value'
          }
        }}
        optionalValue={{
          amount: refundTotal,
          optionalClass: 'total-row-spacing',
          attributes: {
            'data-testing-id': 'cancel-total-optional-value'
          }
        }}
        currencyType={currencyType}
      />
      <BorderTopLineItem />
      <LineItem
        label={{ content: intl.formatMessage({ id: 'inbox_id_cancel_booking.cancel_summary_calculator.refund_amount', defaultMessage: 'Refund amount' }) }}
        value={{}}
        optionalValue={{
          amount: refundTotal,
          optionalClass: 'price-difference-spacing',
          attributes: {
            'data-testing-id': 'cancel-price-difference-optional-value'
          }
        }}
        customClass="price-difference h5"
        currencyType={currencyType}
      />
    </RevisedCalculator>
  );
};

const CancelContractSummaryCalculator = ({ contractObj, refundContractObj, currencyType, paymentDetails, creditRefundSelected, intl }) => {
  const spaceRental = find(contractObj, { type: 'SPACE_RENTAL' }) || {};
  const contractBooking = get(spaceRental, 'meta.booking', {});
  const masterContract = find(contractObj, { type: 'MASTER' }) || {};
  const masterRefundContract = find(refundContractObj?.contracts, { type: 'MASTER' }) || {};
  const updateDifference = get(masterRefundContract, 'calculations.update_difference', 0);
  const discounts = get(contractBooking, 'discounts', {});
  const otherDiscounts = [];
  const additionalCharges = get(contractBooking, 'addtl_charges', []);
  const isCleaningFee = find(additionalCharges, { type: 'CLEANING_FEE' }) || {};
  const cleaningFee = isCleaningFee ? parseFloat(isCleaningFee.value).toFixed(2) : 0;

  const isSplitPayment = get(masterContract, 'split_payment_checkout', false);
  const splitPaymentFeeAmount = parseFloat(get(masterContract, 'calculations.guest.split_payment_fee_amount', 0)).toFixed(2);

  const addOnsTotal = get(masterContract, 'calculations.add_ons.amount', 0);
  const guestAmounts = get(masterContract, 'calculations.guest', {});

  const spaceRentalContract = find(refundContractObj.contracts, { type: 'SPACE_RENTAL' }) || {};
  const cancelDetails = get(spaceRentalContract, 'meta.cancel_details', {});

  const addOnsRefund = get(refundContractObj, 'delta.add_ons.amount', 0);
  const spaceRentalRefundObj = get(refundContractObj, 'delta.space_rentals', {});
  const cleaningFeeRefund = get(refundContractObj, 'delta.cleaning_fee', 0);
  const guestRefundAmounts = get(refundContractObj, 'delta.guest', {});

  const updateDiff = get(masterRefundContract, 'calculations.update_difference', {});
  const creditRefundAmount = get(paymentDetails, 'credit_amount', 0);
  const cashRefundAmount = get(updateDiff, 'refund_accounting_for_scheduled_payment', 0);
  const creditIncentive = get(paymentDetails, 'credit_incentive_amount', 0);

  const originalSpaceRentalSalesTax = get(
    masterContract,
    'calculations.guest.us_sales_tax_space_rental',
    0
  );
  const refundedSpaceRentalSalesTax = Math.abs(
    get(updateDifference, 'guest_us_space_rental_tax')
  );

  const originalAddOnsSalesTax = get(
    masterContract,
    'calculations.guest.us_sales_tax_add_ons',
    0
  );
  const refundedAddOnsSalesTax = Math.abs(
    get(updateDifference, 'guest_us_add_on_tax')
  );

  return (
    <RevisedCalculator>
      <TableLabel
        firstCaption={{
          value: intl.formatMessage({ id: 'inbox_id_cancel_booking.cancel_contract_summary_calculator.price', defaultMessage: 'Price' }),
          optionalClass: 'table-label'
        }}
        secondCaption={{
          value: intl.formatMessage({ id: 'inbox_id_cancel_booking.cancel_contract_summary_calculator.amount', defaultMessage: '{refund} amount' }, {
            refund: (creditRefundAmount && !cashRefundAmount) || creditRefundSelected
              ? intl.formatMessage({ id: 'inbox_id_cancel_booking.cancel_contract_summary_calculator.label_credit', defaultMessage: 'Credit' })
              : intl.formatMessage({ id: 'inbox_id_cancel_booking.cancel_contract_summary_calculator.label_refund', defaultMessage: 'Refund' })
          }),
          optionalClass: 'right-data table-label'
        }}
        attributes={{
          'data-testing-id': 'cancel-summary-table-label'
        }}
      />

      <LineItem
        label={{
          content: intl.formatMessage({ id: 'inbox_id_cancel_booking.cancel_contract_summary_calculator.space_rental', defaultMessage: 'Space Rental' }),
          optionalClass: 'top-vertical-align'
        }}
        value={{
          amount: parseFloat(contractBooking.subtotal_amount || 0).toFixed(2),
          optionalClass: 'top-vertical-align',
          attributes: {
            'data-testing-id': 'cancel-space-rental-value'
          }
        }}
        optionalValue={{
          customValue:
            <div className="margin-md-bottom">
              <FormattedNumber value={parseFloat(spaceRentalRefundObj.guest_subtotal_payment_amount || 0)} currency={currencyType} style='currency' />
              <div className="text-small">
                {0 < spaceRentalRefundObj.guest_subtotal_payment_amount
                  ? <FormattedMessage id="inbox_id_cancel_booking.cancel_contract_summary_calculator.credit_refund" defaultMessage="({refundPercentage} {refundSelected})" values={{
                    refundPercentage: cancelDetails.refund_percentage,
                    refundSelected: creditRefundSelected
                      ? <FormattedMessage id="inbox_id_cancel_booking.cancel_contract_summary_calculator.credit_refund_credit" defaultMessage="credit refund" />
                      : <FormattedMessage id="inbox_id_cancel_booking.cancel_contract_summary_calculator.credit_refund_refund" defaultMessage="refund" />
                  }} />
                  : <FormattedMessage id="inbox_id_cancel_booking.cancel_contract_summary_calculator.credit_refund_no_refund" defaultMessage="(No refund)" />
                }
              </div>
            </div>,
          attributes: {
            'data-testing-id': 'cancel-space-rental-optional-value'
          }
        }}
        currencyType={currencyType}
      />

      {originalSpaceRentalSalesTax > 0 && (
        <LineItem
          label={{
            content: intl.formatMessage({
              id: 'price_table.space_rental_sales_tax',
              defaultMessage: 'Space rental sales tax'
            }),
            optionalClass: 'top-vertical-align'
          }}
          value={{
            amount: parseFloat(originalSpaceRentalSalesTax).toFixed(2),
            optionalClass: 'top-vertical-align',
            attributes: {
              'data-testing-id': 'space-rental-tax-refund-value'
            }
          }}
          currencyType={currencyType}
          optionalValue={{
            customValue: (
              <div className='margin-md-bottom'>
                <FormattedNumber
                  value={parseFloat(refundedSpaceRentalSalesTax).toFixed(2)}
                  currency={currencyType}
                  style={'currency'}
                />
                <div className='text-small'>
                  {refundedSpaceRentalSalesTax > 0 ? (
                    <FormattedMessage
                      id='inbox_id_cancel_booking.cancel_booking_main.refund'
                      defaultMessage='{refund}'
                      values={{
                        refund: (
                          <FormattedMessage
                            id='inbox_id_cancel_booking.cancel_booking_main.percent_refund'
                            defaultMessage='({percent} refund)'
                            values={{ percent: cancelDetails.refund_percentage }}
                          />
                        )
                      }}
                    />
                  ) : (
                    <FormattedMessage
                      id="inbox_id_cancel_booking.cancel_contract_summary_calculator.addons_refund_no_refund"
                      defaultMessage="(No refund)" 
                    />
                  )}
                </div>
              </div>
            ),
            attributes: {
              'data-testing-id': 'space-rental-tax-refund-optional-value'
            }
          }}
        />
      )}

      { addOnsTotal
        ? <LineItem
          label={{
            content: intl.formatMessage({ id: 'inbox_id_cancel_booking.cancel_contract_summary_calculator.addons', defaultMessage: 'Add-ons' }),
            optionalClass: 'top-vertical-align'
          }}
          value={{
            amount: parseFloat(addOnsTotal).toFixed(2),
            optionalClass: 'top-vertical-align',
            attributes: {
              'data-testing-id': 'cancel-add-ons-value'
            }
          }}
          optionalValue={{
            customValue:
              <div className="margin-md-bottom">
                <FormattedNumber value={parseFloat(addOnsRefund).toFixed(2)} currency={currencyType} style='currency' />
                <div className="text-small">
                  {0 < addOnsRefund
                    ? <FormattedMessage id="inbox_id_cancel_booking.cancel_contract_summary_calculator.addons_refund" defaultMessage="({refundPercentage} {refundSelected})" values={{
                      refundPercentage: cancelDetails.refund_percentage,
                      refundSelected: creditRefundSelected
                        ? <FormattedMessage id="inbox_id_cancel_booking.cancel_contract_summary_calculator.addons_refund_credit" defaultMessage="credit refund" />
                        : <FormattedMessage id="inbox_id_cancel_booking.cancel_contract_summary_calculator.addons_refund_refund" defaultMessage="refund" />
                    }} />
                    : <FormattedMessage id="inbox_id_cancel_booking.cancel_contract_summary_calculator.addons_refund_no_refund" defaultMessage="(No refund)" />
                  }
                </div>
              </div>,
            attributes: {
              'data-testing-id': 'cancel-add-ons-optional-value'
            }
          }}
          currencyType={currencyType}
          attributes={{
            'data-testing-id': 'cancel-addons'
          }}
        /> : null
      }

      {originalAddOnsSalesTax > 0 && (
        <LineItem
          label={{
            content: intl.formatMessage({
              id: 'price-breakdown.add-on-sales-tax',
              defaultMessage: 'Add-on sales tax'
            }),
            optionalClass: 'top-vertical-align'
          }}
          value={{
            amount: parseFloat(originalAddOnsSalesTax).toFixed(2),
            optionalClass: 'top-vertical-align',
            attributes: {
              'data-testing-id': 'add-on-sales-tax-refund-value'
            }
          }}
          currencyType={currencyType}
          optionalValue={{
            customValue: (
              <div className='margin-md-bottom'>
                <FormattedNumber
                  value={parseFloat(refundedAddOnsSalesTax).toFixed(2)}
                  currency={currencyType}
                  style={'currency'}
                />
                <div className='text-small'>
                  {refundedAddOnsSalesTax > 0 ? (
                    <FormattedMessage
                      id='inbox_id_cancel_booking.cancel_booking_main.refund'
                      defaultMessage='{refund}'
                      values={{
                        refund: (
                          <FormattedMessage
                            id='inbox_id_cancel_booking.cancel_booking_main.percent_refund'
                            defaultMessage='({percent} refund)'
                            values={{ percent: cancelDetails.refund_percentage }}
                          />
                        )
                      }}
                    />
                  ) : (
                    <FormattedMessage
                      id="inbox_id_cancel_booking.cancel_contract_summary_calculator.addons_refund_no_refund"
                      defaultMessage="(No refund)" 
                    />
                  )}
                </div>
              </div>
            ),
            attributes: {
              'data-testing-id': 'add-on-sales-tax-refund-optional-value'
            }
          }}
        />
      )}

      {
        discounts.map((discount) => {
          const currentDiscount = PrepareDiscountFields(discount);
          const discountLineItem = (
            <LineItem
              label={{
                content: currentDiscount.content,
                optionalClass: 'top-vertical-align'
              }}
              value={{
                amount: parseFloat(`-${currentDiscount.value || 0}`).toFixed(2),
                optionalClass: 'top-vertical-align'
              }}
              optionalValue={{
                amount: 'DAY_RATE_DISCOUNT' === discount.type ? `-${parseFloat(spaceRentalRefundObj['8hr_discounts']).toFixed(2)}` : `-${parseFloat(spaceRentalRefundObj.entitlement_discounts).toFixed(2)}`,
                optionalClass: 'top-vertical-align'
              }}
              currencyType={currencyType}
              customClass="discount-row"
            />
          );

          if ('DAY_RATE_DISCOUNT' === discount.type) {
            return discountLineItem;
          }

          otherDiscounts.push(discountLineItem);
        })
      }

      { (0 < cleaningFee)
        && <LineItem
          label={{
            content: intl.formatMessage({ id: 'inbox_id_cancel_booking.cancel_contract_summary_calculator.cleaning', defaultMessage: 'Cleaning' }),
            optionalClass: 'top-vertical-align',
            optionalTooltip: TooltipData.CLEANING_FEE
          }}
          value={{
            amount: parseFloat(cleaningFee).toFixed(2),
            optionalClass: 'top-vertical-align',
            attributes: {
              'data-testing-id': 'cancel-cleaning-fee-value'
            }
          }}
          optionalValue={{
            customValue:
              <div className="margin-md-bottom">
                <FormattedNumber value={parseFloat(cleaningFeeRefund).toFixed(2)} currency={currencyType} style='currency' />
                <div className="text-small">
                  <FormattedMessage
                    id="inbox_id_cancel_booking.cancel_contract_summary_calculator.cleaning_refund"
                    defaultMessage="(100% {selected})"
                    values={{
                      selected: creditRefundSelected
                        ? <FormattedMessage id="inbox_id_cancel_booking.cancel_contract_summary_calculator.cleaning_refund_credit" defaultMessage="credit refund" />
                        : <FormattedMessage id="inbox_id_cancel_booking.cancel_contract_summary_calculator.cleaning_refund_refund" defaultMessage="refund" />
                    }}
                  />
                </div>
              </div>,
            optionalClass: 'top-vertical-align',
            attributes: {
              'data-testing-id': 'cancel-cleaning-fee-optional-value'
            }
          }}
          currencyType={currencyType}
          attributes={{
            'data-testing-id': 'cancel-cleaning-fee'
          }}
        />
      }
      {
        otherDiscounts.map((lineItem => {
          return lineItem;
        }))
      }
      <LineItem
        label={{
          content: intl.formatMessage({ id: 'inbox_id_cancel_booking.cancel_contract_summary_calculator.processing', defaultMessage: 'Processing' }),
          optionalTooltip: TooltipData.PROCESSING_FEE,
          optionalClass: 'top-vertical-align'
        }}
        value={{
          amount: parseFloat(guestAmounts.fee || 0).toFixed(2),
          optionalClass: 'top-vertical-align',
          attributes: {
            'data-testing-id': 'cancel-processing-fee-value'
          }
        }}
        optionalValue={{
          customValue:
            <div className="margin-md-bottom">
              <FormattedNumber value={parseFloat(guestRefundAmounts.fee || 0).toFixed(2)} currency={currencyType} style={'currency'} />
              <div className="text-small">
                {0 < guestRefundAmounts.fee
                  ? <FormattedMessage id="inbox_id_cancel_booking.cancel_contract_summary_calculator.guest_refund" defaultMessage="(100% {selected})" values={{
                    selected: creditRefundSelected
                      ? <FormattedMessage id="inbox_id_cancel_booking.cancel_contract_summary_calculator.guest_refund_credit" defaultMessage="credit refund" />
                      : <FormattedMessage id="inbox_id_cancel_booking.cancel_contract_summary_calculator.guest_refund_refund" defaultMessage="refund" />
                  }} />
                  : <FormattedMessage id="inbox_id_cancel_booking.cancel_contract_summary_calculator.guest_refund_no_refund" defaultMessage="(No refund)" />
                }
              </div>
            </div>,
          optionalClass: 'top-vertical-align margin-sm-bottom',
          attributes: {
            'data-testing-id': 'cancel-processing-fee-optional-value'
          }
        }}
        currencyType={currencyType}
      />

      { 0 < creditIncentive
        && <LineItem
          label={{
            content: intl.formatMessage({ id: 'inbox_id_cancel_booking.cancel_contract_summary_calculator.credit_bonus', defaultMessage: 'Credit Bonus (+20%)' }),
            optionalClass: 'top-vertical-align text-green'
          }}
          value={{
            amount: ''
          }}
          optionalValue={{
            amount: parseFloat(creditIncentive).toFixed(2),
            optionalClass: 'text-green margin-sm-bottom',
            attributes: {
              'data-testing-id': 'cancel-credit-incentive-optional-value'
            }
          }}
          currencyType={currencyType}
          attributes={{
            'data-testing-id': 'cancel-credit-incentive'
          }}
        /> }

      { isSplitPayment && 0 < splitPaymentFeeAmount
        && <LineItem
          label={{
            content: intl.formatMessage({ id: 'inbox_id_cancel_booking.cancel_contract_summary_calculator.split_payment_fee', defaultMessage: 'Pay part now, part later fee' }),
            optionalClass: 'top-vertical-align'
          }}
          value={{
            amount: splitPaymentFeeAmount,
            optionalClass: 'top-vertical-align',
            attributes: {
              'data-testing-id': 'cancel-split-payment-value'
            }
          }}
          optionalValue={{
            customValue:
              <div className="margin-md-bottom">
                <FormattedNumber value={splitPaymentFeeAmount} currency={currencyType} style="currency" />
                <div className="text-small">
                  <FormattedMessage
                    id="inbox_id_cancel_booking.cancel_contract_summary_calculator.split_payment_fee_refund_amount"
                    defaultMessage="(100% {selected})"
                    values={{
                      selected: creditRefundSelected
                        ? <FormattedMessage id="inbox_id_cancel_booking.cancel_contract_summary_calculator.split_payment_fee_refund_credit" defaultMessage="credit refund" />
                        : <FormattedMessage id="inbox_id_cancel_booking.cancel_contract_summary_calculator.split_payment_fee_refund" defaultMessage="refund" />
                    }}
                  />
                </div>
              </div>,
            optionalClass: 'top-vertical-align',
            attributes: {
              'data-testing-id': 'cancel-split-payment-optional-value'
            }
          }}
          currencyType={currencyType}
          attributes={{
            'data-testing-id': 'cancel-split-payment'
          }}
        />
      }

      <BorderBottomLineItem />
      <LineItem
        label={{
          content: intl.formatMessage({ id: 'inbox_id_cancel_booking.cancel_contract_summary_calculator.total', defaultMessage: 'Total' })
        }}
        value={{
          amount: parseFloat(guestAmounts.payment || 0).toFixed(2),
          optionalClass: 'remove-vertical-align',
          attributes: {
            'data-testing-id': 'cancel-total-value'
          }
        }}
        optionalValue={{
          amount: parseFloat(creditRefundSelected ? creditRefundAmount : guestRefundAmounts.payment || 0).toFixed(2),
          optionalClass: 'total-row-spacing',
          attributes: {
            'data-testing-id': 'cancel-total-optional-value'
          }
        }}
        currencyType={currencyType}
      />
      <BorderTopLineItem />

      { 0 < creditRefundAmount
        && <LineItem
          label={{
            content: intl.formatMessage({ id: 'inbox_id_cancel_booking.cancel_contract_summary_calculator.credit_amount', defaultMessage: 'Credit amount' }),
            // only show if mixed refund
            optionalTooltip: creditRefundAmount && cashRefundAmount ? TooltipData.PARTIAL_CREDIT_REFUND : null
          }}
          value={{}}
          optionalValue={{
            amount: parseFloat(creditRefundAmount).toFixed(2),
            optionalClass: 'price-difference-spacing',
            attributes: {
              'data-testing-id': 'cancel-credits-optional-value'
            }
          }}
          customClass="price-difference h5"
          currencyType={currencyType}
          attributes={{
            'data-testing-id': 'cancel-credits'
          }}
        /> }

      { (0 < cashRefundAmount || !creditRefundAmount) && !creditRefundSelected
        && <LineItem
          label={{
            content: intl.formatMessage({ id: 'inbox_id_cancel_booking.cancel_contract_summary_calculator.refund_amount', defaultMessage: 'Refund amount' })
          }}
          value={{}}
          optionalValue={{
            amount: parseFloat(cashRefundAmount).toFixed(2),
            optionalClass: 'price-difference-spacing',
            attributes: {
              'data-testing-id': 'cancel-refund-optional-value'
            }
          }}
          customClass="price-difference h5"
          currencyType={currencyType}
          attributes={{
            'data-testing-id': 'cancel-refund'
          }}
        /> }
    </RevisedCalculator>
  );
};

const HostCancelSummaryCalculatorFnb = ({ bookingObj, payoutBookingObj, currencyType, intl }) => {
  const {
    listing = {},
    host_payout_amount,
    base_subtotal,
    gratuity,
    sales_tax
  } = bookingObj;
  const payoutTotal = parseFloat(payoutBookingObj.cancel_host_payout || payoutBookingObj.host_payout_amount || 0).toFixed(2);
  // for fee
  const feeTotal = parseFloat(bookingObj.host_payout_processing_fee || 0).toFixed(2);
  // for fnb subtotal breakdown
  const payoutTip = parseFloat(payoutBookingObj.cancel_host_gratuity || payoutBookingObj.gratuity || 0).toFixed(2);
  const payoutTax = parseFloat(payoutBookingObj.cancel_host_sales_tax || payoutBookingObj.sales_tax || 0).toFixed(2);
  const payoutBaseSubtotal = parseFloat(payoutBookingObj.cancel_host_base_subtotal || payoutBookingObj.base_subtotal || 0).toFixed(2);
  const subtotalLabel = 'FOOD_BEV' === listing.fandb_applies_to
    ? intl.formatMessage({ id: 'inbox_id_cancel_booking.host_cancel_summary_calculator_fnb.food_bar_tab', defaultMessage: 'Food and bar tab' })
    : intl.formatMessage({ id: 'inbox_id_cancel_booking.host_cancel_summary_calculator_fnb.bar_tab', defaultMessage: 'Bar tab' });

  return (
    <RevisedCalculator>
      <TableLabel
        firstCaption={{
          value: intl.formatMessage({ id: 'inbox_id_cancel_booking.host_cancel_summary_calculator_fnb.original_payout', defaultMessage: 'Original payout' }),
          optionalClass: 'table-label'
        }}
        secondCaption={{
          value: intl.formatMessage({ id: 'inbox_id_cancel_booking.host_cancel_summary_calculator_fnb.new_payout', defaultMessage: 'New payout' }),
          optionalClass: 'right-data table-label'
        }}
      />
      <LineItem
        label={{ content: subtotalLabel }}
        value={{ amount: parseFloat(bookingObj.subtotal_amount || 0).toFixed(2) }}
        optionalValue={{ amount: parseFloat(payoutBookingObj.cancel_host_payout_subtotal || payoutBookingObj.subtotal_amount || 0).toFixed(2) }}
        currencyType={currencyType}
      />
      <BorderTopLineItem />
      <LineItem
        label={{
          content: intl.formatMessage({ id: 'inbox_id_cancel_booking.host_cancel_summary_calculator_fnb.subtotal', defaultMessage: 'Subtotal' }),
          optionalClass: 'fnb-subtotal-spacing indent-fnb-label top-vertical-align'
        }}
        value={{
          amount: parseFloat(base_subtotal || 0).toFixed(2),
          optionalClass: 'fnb-subtotal-spacing top-vertical-align'
        }}
        optionalValue={{
          amount: payoutBaseSubtotal,
          optionalClass: 'fnb-subtotal-spacing'
        }}
        currencyType={currencyType}
        customClass="fnb-font-size"
      />
      <LineItem
        label={{
          content: intl.formatMessage({ id: 'inbox_id_cancel_booking.host_cancel_summary_calculator_fnb.tax', defaultMessage: 'Tax' }),
          optionalClass: 'indent-fnb-label top-vertical-align'
        }}
        value={{
          amount: parseFloat(sales_tax || 0).toFixed(2),
          optionalClass: 'top-vertical-align'
        }}
        optionalValue={{
          amount: payoutTax,
          optionalClass: 'top-vertical-align'
        }}
        currencyType={currencyType}
        customClass="fnb-font-size"
      />
      <LineItem
        label={{
          content: intl.formatMessage({ id: 'inbox_id_cancel_booking.host_cancel_summary_calculator_fnb.tip', defaultMessage: 'Tip' }),
          optionalClass: 'indent-fnb-label top-vertical-align'
        }}
        value={{
          amount: parseFloat(gratuity || 0).toFixed(2),
          optionalClass: 'top-vertical-align'
        }}
        optionalValue={{
          amount: payoutTip,
          optionalClass: 'top-vertical-align'
        }}
        currencyType={currencyType}
        customClass="fnb-font-size"
      />
      <BorderTopLineItem />
      <LineItem
        label={{
          content: intl.formatMessage({ id: 'inbox_id_cancel_booking.host_cancel_summary_calculator_fnb.service_fee', defaultMessage: 'Service Fee' }),
          optionalTooltip: TooltipData.SERVICE_FEE,
          optionalClass: 'fnb-processing-fee top-vertical-align'
        }}
        value={{
          amount: `-${feeTotal}`,
          optionalClass: 'fnb-processing-fee top-vertical-align'
        }}
        optionalValue={{
          amount: `-${parseFloat(payoutBookingObj.cancel_host_processing_fee || payoutBookingObj.host_payout_processing_fee || 0).toFixed(2)}`,
          optionalClass: 'fnb-processing-fee top-vertical-align'
        }}
        currencyType={currencyType}
      />
      <BorderTopLineItem />
      <LineItem
        label={{
          content: intl.formatMessage({ id: 'inbox_id_cancel_booking.host_cancel_summary_calculator_fnb.total', defaultMessage: 'Total' })
        }}
        value={{
          amount: parseFloat(host_payout_amount || 0).toFixed(2),
          optionalClass: 'remove-vertical-align'
        }}
        optionalValue={{
          amount: payoutTotal,
          optionalClass: `total-row-spacing ${payoutTotal && 'updated-total-price'}`
        }}
        currencyType={currencyType}
      />
      <BorderTopLineItem />
      <LineItem
        label={{
          content: intl.formatMessage({ id: 'inbox_id_cancel_booking.host_cancel_summary_calculator_fnb.new_payout', defaultMessage: 'New payout' })
        }}
        value={{}}
        optionalValue={{
          amount: payoutTotal,
          optionalClass: 'price-difference-spacing'
        }}
        customClass="price-difference h5"
        currencyType={currencyType}
      />
    </RevisedCalculator>
  );
};


const HostCancelSummaryCalculator = ({ bookingObj, payoutBookingObj, currencyType, intl }) => {
  const {
    discounts,
    addtl_charges,
    host_payout_amount
  } = bookingObj;

  const payoutTotal = parseFloat(payoutBookingObj.cancel_host_payout).toFixed(2);
  // for fee
  const feeTotal = parseFloat(bookingObj.host_payout_processing_fee).toFixed(2);

  return (
    <RevisedCalculator>
      <TableLabel
        firstCaption={{
          value: intl.formatMessage({ id: 'inbox_id_cancel_booking.host_cancel_summary_calculator.original_payout', defaultMessage: 'Original payout' }),
          optionalClass: 'table-label'
        }}
        secondCaption={{
          value: intl.formatMessage({ id: 'inbox_id_cancel_booking.host_cancel_summary_calculator.new_payout', defaultMessage: 'New payout' }),
          optionalClass: 'right-data table-label'
        }}
      />
      <LineItem
        label={{
          content: intl.formatMessage({ id: 'inbox_id_cancel_booking.host_cancel_summary_calculator.subtotal', defaultMessage: 'Subtotal' }),
          optionalClass: 'top-vertical-align'
        }}
        value={{
          amount: parseFloat(bookingObj.subtotal_amount).toFixed(2),
          optionalClass: 'top-vertical-align'
        }}
        optionalValue={{
          amount: parseFloat(payoutBookingObj.cancel_host_payout_subtotal).toFixed(2)
        }}
        currencyType={currencyType}
      />
      {
        addtl_charges.map((charge) => {
          // cleaning fees are always refunded, never paid out
          return (
            <LineItem
              label={{
                content: charge.charge_str,
                ...(TooltipData[charge.type] && { optionalTooltip: TooltipData[charge.type] }),
                optionalClass: 'top-vertical-align'
              }}
              value={{
                amount: parseFloat(charge.value).toFixed(2),
                optionalClass: 'top-vertical-align'
              }}
              optionalValue={{
                amount: parseFloat(payoutBookingObj.cancel_host_cleaning_fee).toFixed(2),
                optionalClass: 'top-vertical-align'
              }}
              currencyType={currencyType}
            />
          );
        })
      }
      {
        discounts.map((discount) => {
          if ('DAY_RATE_DISCOUNT' !== discount.type) {
            return null;
          }
          const currentDiscount = PrepareDiscountFields(discount);
          return (
            <LineItem
              label={{
                content: currentDiscount.content,
                optionalClass: 'top-vertical-align'
              }}
              value={{
                amount: parseFloat(`-${currentDiscount.value}`).toFixed(2),
                optionalClass: 'top-vertical-align'
              }}
              optionalValue={{
                amount: `-${parseFloat(payoutBookingObj.cancel_host_day_rate_discount).toFixed(2)}`,
                optionalClass: 'top-vertical-align'
              }}
              currencyType={currencyType}
              customClass="discount-row"
            />
          );
        })
      }
      <LineItem
        label={{
          content: intl.formatMessage({ id: 'inbox_id_cancel_booking.host_cancel_summary_calculator.service_fee', defaultMessage: 'Service Fee' }),
          optionalTooltip: TooltipData.SERVICE_FEE,
          optionalClass: 'top-vertical-align'
        }}
        value={{
          amount: `-${feeTotal}`,
          optionalClass: 'top-vertical-align'
        }}
        optionalValue={{
          amount: `-${parseFloat(payoutBookingObj.cancel_host_processing_fee || 0).toFixed(2)}`,
          optionalClass: 'top-vertical-align margin-sm-bottom'
        }}
        currencyType={currencyType}
      />
      <BorderBottomLineItem />
      <LineItem
        label={{
          content: intl.formatMessage({ id: 'inbox_id_cancel_booking.host_cancel_summary_calculator.total', defaultMessage: 'Total' })
        }}
        value={{
          amount: parseFloat(host_payout_amount).toFixed(2),
          optionalClass: 'remove-vertical-align'
        }}
        optionalValue={{
          amount: payoutTotal,
          optionalClass: 'total-row-spacing'
        }}
        currencyType={currencyType}
      />
      <BorderTopLineItem />
      <LineItem
        label={{
          content: intl.formatMessage({ id: 'inbox_id_cancel_booking.host_cancel_summary_calculator.new_payout', defaultMessage: 'New payout' })
        }}
        value={{}}
        optionalValue={{
          amount: payoutTotal,
          optionalClass: 'price-difference-spacing'
        }}
        customClass="price-difference h5"
        currencyType={currencyType}
      />
    </RevisedCalculator>
  );
};

const HostCancelContractSummaryCalculator = ({ contractObj, payoutBookingObj, currencyType, intl }) => {
  const masterContract = find(contractObj, { type: 'MASTER' }) || {};
  const spaceRental = find(contractObj, { type: 'SPACE_RENTAL' }) || {};
  const contractBooking = get(spaceRental, 'meta.booking', {});
  const discounts = get(contractBooking, 'discounts', {});
  const additionalCharges = get(contractBooking, 'addtl_charges', []);
  const isCleaningFee = find(additionalCharges, { type: 'CLEANING_FEE' }) || {};
  const cleaningFee = isCleaningFee ? parseFloat(isCleaningFee.value).toFixed(2) : 0;
  const addOnsTotal = get(masterContract, 'calculations.add_ons.amount', 0);
  const hostAmounts = get(masterContract, 'calculations.host', {});

  const currentMasterContract = find(payoutBookingObj, { type: 'MASTER' }) || {};
  const currentSpaceRental = find(payoutBookingObj, { type: 'SPACE_RENTAL' }) || {};
  const currentContractBooking = get(currentSpaceRental, 'meta.booking', {});
  const currentHostAmounts = get(currentMasterContract, 'calculations.host', {});
  const currentAdditionalCharges = get(currentContractBooking, 'addtl_charges', []);
  const currentDiscounts = get(currentContractBooking, 'discounts', {});
  const currentDayRateDiscount = find(currentDiscounts, { type: 'DAY_RATE_DISCOUNT' }) || {};
  const currentIsCleaningFee = find(currentAdditionalCharges, { type: 'CLEANING_FEE' }) || {};
  const currentCleaningFee = currentIsCleaningFee ? parseFloat(currentIsCleaningFee.value).toFixed(2) : 0;
  const currentAddOnsTotal = get(currentMasterContract, 'calculations.add_ons.amount', 0);

  return (
    <RevisedCalculator>
      <TableLabel
        firstCaption={{
          value: intl.formatMessage({ id: 'inbox_id_cancel_booking.host_cancel_contract_summary_calculator.original_payout', defaultMessage: 'Original payout' }),
          optionalClass: 'table-label'
        }}
        secondCaption={{
          value: intl.formatMessage({ id: 'inbox_id_cancel_booking.host_cancel_contract_summary_calculator.new_payout', defaultMessage: 'New payout' }),
          optionalClass: 'right-data table-label'
        }}
      />
      <LineItem
        label={{
          content: intl.formatMessage({ id: 'inbox_id_cancel_booking.host_cancel_contract_summary_calculator.space_rental', defaultMessage: 'Space Rental' }),
          optionalClass: 'top-vertical-align'
        }}
        value={{
          amount: parseFloat(contractBooking.subtotal_amount).toFixed(2),
          optionalClass: 'top-vertical-align'
        }}
        optionalValue={{
          amount: parseFloat(currentContractBooking.subtotal_amount).toFixed(2)
        }}
        currencyType={currencyType}
      />

      { addOnsTotal
        ? <LineItem
          label={{
            content: intl.formatMessage({ id: 'inbox_id_cancel_booking.host_cancel_contract_summary_calculator.addons', defaultMessage: 'Add-ons' }),
            optionalClass: 'top-vertical-align'
          }}
          value={{
            amount: parseFloat(addOnsTotal).toFixed(2),
            optionalClass: 'top-vertical-align'
          }}
          optionalValue={{
            customValue:
              <div className="margin-md-bottom">
                <FormattedNumber value={parseFloat(currentAddOnsTotal).toFixed(2)} currency={currencyType} style={'currency'} />
              </div>
          }}
          currencyType={currencyType}
        /> : null
      }
      { (0 < cleaningFee)
        // cleaning fees are always refunded, never paid out
        && <LineItem
          label={{
            content: intl.formatMessage({ id: 'inbox_id_cancel_booking.host_cancel_contract_summary_calculator.cleaning', defaultMessage: 'Cleaning' }),
            optionalClass: 'top-vertical-align',
            optionalTooltip: TooltipData.CLEANING_FEE
          }}
          value={{
            amount: parseFloat(cleaningFee).toFixed(2),
            optionalClass: 'top-vertical-align'
          }}
          optionalValue={{
            customValue:
              <div className="margin-md-bottom">
                <FormattedNumber value={parseFloat(currentCleaningFee).toFixed(2)} currency={currencyType} style={'currency'} />
                <div className="text-small">
                  <FormattedMessage id='inbox_id_cancel_booking.host_cancel_contract_summary_calculator.cleaning_refund' defaultMessage='(100% refund)' />
                </div>
              </div>,
            optionalClass: 'top-vertical-align'
          }}
          currencyType={currencyType}
        />
      }
      {
        discounts.map((discount) => {
          if ('DAY_RATE_DISCOUNT' !== discount.type) {
            return null;
          }
          const currentDiscount = PrepareDiscountFields(discount);
          return (
            <LineItem
              label={{
                content: currentDiscount.content,
                optionalClass: 'top-vertical-align'
              }}
              value={{
                amount: parseFloat(`-${currentDiscount.value}`).toFixed(2),
                optionalClass: 'top-vertical-align'
              }}
              optionalValue={{
                amount: `-${parseFloat(currentDayRateDiscount.discount_amount).toFixed(2)}`,
                optionalClass: 'top-vertical-align'
              }}
              currencyType={currencyType}
              customClass="discount-row"
            />
          );
        })
      }
      <LineItem
        label={{
          content: intl.formatMessage({ id: 'inbox_id_cancel_booking.host_cancel_contract_summary_calculator.service_fee', defaultMessage: 'Service Fee' }),
          optionalTooltip: TooltipData.SERVICE_FEE,
          optionalClass: 'top-vertical-align'
        }}
        value={{
          amount: `-${parseFloat(hostAmounts.fee).toFixed(2)}`,
          optionalClass: 'top-vertical-align'
        }}
        optionalValue={{
          amount: `-${parseFloat(currentHostAmounts.fee).toFixed(2)}`,
          optionalClass: 'top-vertical-align margin-sm-bottom'
        }}
        currencyType={currencyType}
      />
      <BorderBottomLineItem />
      <LineItem
        label={{
          content: intl.formatMessage({ id: 'inbox_id_cancel_booking.host_cancel_contract_summary_calculator.total', defaultMessage: 'Total' })
        }}
        value={{
          amount: parseFloat(hostAmounts.payout).toFixed(2),
          optionalClass: 'remove-vertical-align'
        }}
        optionalValue={{
          amount: parseFloat(currentHostAmounts.payout).toFixed(2),
          optionalClass: 'total-row-spacing'
        }}
        currencyType={currencyType}
      />
      <BorderTopLineItem />
      <LineItem
        label={{
          content: intl.formatMessage({ id: 'inbox_id_cancel_booking.host_cancel_contract_summary_calculator.new_payout', defaultMessage: 'New payout' })
        }}
        value={{}}
        optionalValue={{
          amount: parseFloat(currentHostAmounts.payout).toFixed(2),
          optionalClass: 'price-difference-spacing'
        }}
        customClass="price-difference h5"
        currencyType={currencyType}
      />
    </RevisedCalculator>
  );
};

exports.HostCancelContractSummaryCalculator = injectIntl(HostCancelContractSummaryCalculator);
exports.HostCancelSummaryCalculator = injectIntl(HostCancelSummaryCalculator);
exports.HostCancelSummaryCalculatorFnb = injectIntl(HostCancelSummaryCalculatorFnb);
exports.CancelSummaryCalculatorFnb = injectIntl(CancelSummaryCalculatorFnb);
exports.CancelContractSummaryCalculator = injectIntl(CancelContractSummaryCalculator);
exports.CancelSummaryCalculator = injectIntl(CancelSummaryCalculator);
